import { useCallback, useEffect, useId, useMemo, useRef, useState, type SyntheticEvent } from 'react'
import type { AxiosResponse } from 'axios'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import { downloadFile } from 'medere-ui-framework'
import useNotify from '../../hooks/useNotify'
import { exportErrorMessage } from '../../services/reportExport'
import './ExportMenu.scss'

export type ExportFormat = 'pdf' | 'xlsx'

export interface ExportMenuProps {
    /** Pide el archivo al backend en el formato elegido, con los filtros aplicados. Devuelve la respuesta completa. */
    onExport: (format: ExportFormat) => Promise<AxiosResponse<Blob>>
    /** Deshabilita el botón (por ejemplo, mientras no hay un resultado). Requiere `disabledReason`. */
    disabled?: boolean
    /** Por qué no se puede exportar ahora: se muestra en el tooltip del botón deshabilitado. */
    disabledReason?: string
    /** Nombre de archivo, sin extensión, si el backend no manda `Content-Disposition`. */
    fallbackFileName: string
}

/**
 * Botón secundario «Exportar» con un menú «Descargar PDF» / «Descargar Excel». Va en el encabezado de la vista
 * (`nv-head-row`), a la izquierda del primario. El nombre del archivo sale del `Content-Disposition` de la respuesta.
 * Mientras exporta, el botón queda en carga y deshabilitado (con el motivo en el tooltip); si falla, avisa con una
 * card flotante: el mensaje del backend si es un error de negocio (4xx) y, si no, «No se pudo exportar. Intentá de
 * nuevo.».
 *
 * Ejemplo (el pedido usa los filtros aplicados, no el borrador):
 *
 * ```tsx
 * <div className="nv-head-row">
 *   <h1 className="nv-h1">Estadísticas de atención</h1>
 *   <ExportMenu
 *     fallbackFileName="estadisticas-de-atencion"
 *     disabled={!applied}
 *     disabledReason="Elegí un período para exportar"
 *     onExport={format => exportServiceStatistics(applied, format)}
 *   />
 * </div>
 * ```
 */
export default function ExportMenu({ onExport, disabled = false, disabledReason, fallbackFileName }: ExportMenuProps) {
    // TODO(design-system): secundario con menú — Novarium define «Exportar» como secundario pero no uno que abra un menú.
    const intl = useIntl()
    const notify = useNotify()
    const menuId = useId()
    const buttonId = `${menuId}-button`
    const menuRef = useRef<Menu>(null)
    const exportingRef = useRef(false)
    const mountedRef = useRef(true)
    const [exporting, setExporting] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    // Esc cierra el menú y devuelve el foco al botón
    useEffect(() => {
        if (!open) return undefined
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            menuRef.current?.hide(event as unknown as SyntheticEvent) // el listener es del documento: PrimeReact solo usa el evento para notificar
            document.getElementById(buttonId)?.focus()
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open, buttonId])

    const run = useCallback(async (format: ExportFormat) => {
        if (exportingRef.current) return
        exportingRef.current = true
        setExporting(true)
        try {
            const response = await onExport(format)
            await downloadFile(response, `${fallbackFileName}.${format}`)
        } catch (error: unknown) {
            notify.error(await exportErrorMessage(error, intl.formatMessage({ id: 'ReportExportError' })))
        } finally {
            exportingRef.current = false
            if (mountedRef.current) setExporting(false)
        }
    }, [onExport, fallbackFileName, notify, intl])

    const items = useMemo<MenuItem[]>(() => [
        { label: intl.formatMessage({ id: 'ReportExportDownloadPdf' }), icon: 'pi pi-file-pdf', command: () => { void run('pdf') } },
        { label: intl.formatMessage({ id: 'ReportExportDownloadExcel' }), icon: 'pi pi-file-excel', command: () => { void run('xlsx') } },
    ], [intl, run])

    // El menú se abre sin foco en los ítems: se lo lleva al primero para poder usar el teclado
    const focusFirstItem = () => {
        requestAnimationFrame(() => {
            document.getElementById(menuId)?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
        })
    }

    const blocked = disabled || exporting
    const tooltip = exporting
        ? intl.formatMessage({ id: 'ReportExportInProgress' })
        : disabled ? disabledReason : undefined

    return (
        <>
            <Menu
                model={items}
                popup
                ref={menuRef}
                id={menuId}
                className="nv-scope nv-export-menu"
                onShow={() => { setOpen(true); focusFirstItem() }}
                onHide={() => setOpen(false)}
            />
            <Button
                type="button"
                outlined
                className="buttonMain2"
                icon="pi pi-download"
                label={intl.formatMessage({ id: 'ReportExportLabel' })}
                loading={exporting}
                disabled={blocked}
                id={buttonId}
                aria-label={intl.formatMessage({ id: 'ReportExportAria' })}
                aria-haspopup="menu"
                aria-controls={menuId}
                aria-expanded={open}
                tooltip={tooltip}
                tooltipOptions={{ position: 'top', showOnDisabled: true }}
                onClick={event => menuRef.current?.toggle(event)}
            />
        </>
    )
}
